// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — one round of a live race, decoupled from Trystero, the DOM and the clock (driven by
// take()/draw()/tick()), which is what makes the host-transfer contract testable without a network
// (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer plays its OWN copy of a cairn that is a pure function of the
// frozen seed, and reaches its OWN game-over (clear it, run the draw budget out in Scree, or run
// the clock out in Cascade) with no host help. NO board state ever crosses the wire — only a tiny
// status gossip (score + stones cleared + cairns banked as live progress, and on finishing done,
// won, final score, time). HOST TRANSFER IS A PURE DISPLAY CONCERN; onHostChange is wired
// regardless. The winner is whoever scores the most (a clear ranks above a non-clear in single
// modes).

import { createGame, draw as drawGame, drainEvents, take as takeGame, tick as tickGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  cleared: number; // stones cleared across the run (progress)
  cairns: number; // cairns fully cleared (Cascade)
  bestChain: number;
  done: boolean; // their round is over
  won: boolean; // finished successfully (cleared / completed the run)
  ms: number; // finish time
  reason: string | null;
  left: boolean;
}

/** Tiny wire status. */
export interface RaceMsg {
  sc?: number; // score (progress / final)
  cl?: number; // stones cleared (progress)
  py?: number; // cairns banked (progress)
  bc?: number; // best chain
  d?: boolean; // done
  w?: boolean; // won
  fsc?: number; // final score
  ms?: number; // finish time
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a window. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

const PROGRESS_EVENTS = new Set<GameEvent['t']>(['take', 'draw', 'clearCairn', 'next']);

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        score: 0,
        cleared: 0,
        cairns: 0,
        bestChain: 0,
        done: false,
        won: false,
        ms: 0,
        reason: null,
        left: false,
      });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  private syncSelf(): void {
    const me = this.self();
    if (!me) return;
    me.score = this.state.score;
    me.cleared = this.state.totalCleared;
    me.cairns = this.state.cairns;
    me.bestChain = this.state.bestChain;
  }

  private after(events: GameEvent[]): GameEvent[] {
    this.syncSelf();
    if (events.some((e) => PROGRESS_EVENTS.has(e.t))) {
      this.deps.send({ sc: this.state.score, cl: this.state.totalCleared, py: this.state.cairns });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  /** Take a stone. Spectators no-op. */
  take(id: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    takeGame(this.state, id);
    return this.after(drainEvents(this.state));
  }

  /** Draw from the scree. Spectators no-op. */
  draw(): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    drawGame(this.state);
    return this.after(drainEvents(this.state));
  }

  /** Advance the wall-clock. A Cascade timeout ends the round. */
  tick(dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    tickGame(this.state, dtMs);
    const events = drainEvents(this.state);
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.done = true;
      me.won = this.state.finished;
      me.score = this.state.score;
      me.cleared = this.state.totalCleared;
      me.cairns = this.state.cairns;
      me.bestChain = this.state.bestChain;
      me.ms = Math.round(this.state.elapsedMs);
      me.reason = this.state.reason;
    }
    this.deps.send({
      d: true,
      w: this.state.finished,
      fsc: this.state.score,
      cl: this.state.totalCleared,
      py: this.state.cairns,
      bc: this.state.bestChain,
      ms: Math.round(this.state.elapsedMs),
    });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return;
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.sc === 'number') r.score = Math.max(0, msg.sc | 0);
    if (typeof msg.cl === 'number') r.cleared = Math.max(0, msg.cl | 0);
    if (typeof msg.py === 'number') r.cairns = Math.max(0, msg.py | 0);
    if (typeof msg.bc === 'number') r.bestChain = Math.max(0, msg.bc | 0);
    if (msg.d) {
      r.done = true;
      r.won = !!msg.w;
      if (typeof msg.fsc === 'number') r.score = Math.max(0, msg.fsc | 0);
      if (typeof msg.ms === 'number') r.ms = Math.max(0, msg.ms | 0);
    }
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.done) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.done && !r.left) return false;
    return true;
  }

  /** The player who should take the win: the top of the ranking, if they actually scored. */
  winner(): PeerResult | null {
    const top = this.ranked().find((r) => !r.left);
    if (!top) return null;
    if (this.mode.stream) return top.score > 0 ? top : null;
    return top.won || top.score > 0 ? top : null;
  }

  /**
   * Cascade: most score, then most cairns. Single modes: a full clear ranks above a non-clear,
   * then by score, then stones cleared. Departed peers last.
   */
  ranked(): PeerResult[] {
    const stream = this.mode.stream;
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      if (stream) {
        return b.score - a.score || b.cairns - a.cairns || a.seat - b.seat;
      }
      if (a.won !== b.won) return a.won ? -1 : 1;
      return b.score - a.score || b.cleared - a.cleared || a.seat - b.seat;
    });
  }
}
